/*    
<summary>
  This functional component "AddProductManagementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import Popover from "@mui/material/Popover";
import { RichTreeView } from "@mui/x-tree-view";
import dayjs from "dayjs";
import { Field, Form, Formik, useFormikContext } from "formik";
import moment from "moment";
import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { DATE_FORMAT, integerRegex } from "../../../../constants/constants";
import { ActionType } from "../../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../../constants/enums/module-type-enum";
import userTypeEnum from "../../../../constants/enums/user-type-enum";
import { useStore } from "../../../../contexts/store-provider";
import { isOperationPermittedRoleBase } from "../../../../helpers/action-permission-helper";
import { getUserTypeFromLocalStorage } from "../../../../helpers/localstorage-helper";
import { toHalfWidth } from "../../../../helpers/validate-helper";
import { IOption } from "../../../../models/ICommon";
import ProductAutoCalculateTax from "../../../../shared-components/auto-tax-calculation/use-product-auto-calculate-tax";
import LoaderButton from "../../../../shared-components/button/loader-button";
import FormikDateFieldMUI from "../../../../shared-components/FormikFormHandlers/FormikDateFieldMUI";
import FormikFormImagePicker from "../../../../shared-components/FormikFormHandlers/FormikFormImagePicker";
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputRegexCheckFormattedNumber from "../../../../shared-components/FormikFormHandlers/FormikFormInputRegexCheckFormattedNumber";
import FormikFormOnChangeSelect from "../../../../shared-components/FormikFormHandlers/FormikFormOnChangeSelect";
import FormikFormSelect from "../../../../shared-components/FormikFormHandlers/FormikFormSelect";
import FormikFormTextArea from "../../../../shared-components/FormikFormHandlers/FormikFormTextArea";
import {
  AddProductValidationSchema,
  EditProductValidationSchema,
} from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import Label from "../../../../shared-components/label/label";
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";
import { formatMessage } from "../../../../translations/format-message";
import "./add-edit-product-form.css";
import BarcodeScannerModal from "./barcode-scanner-modal";

interface IProps {
  id: number;
  initialValues: any;
  getProductManagementLoading: boolean;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  businessTypeOptions: IOption[];
  categoryOptions: IOption[];
  taxOptions: IOption[];
  registrationTypeOptions: IOption[];
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
  handleDeleteSubmitHandler: () => void;
  deleteActionLoading: boolean;
  viewOnly: boolean;
}

const AddProductManagementForm = (props: IProps) => {
  const {
    showDeletePopup,
    deletePopupToggleHandler,
    handleDeleteSubmitHandler,
    deleteActionLoading,
    viewOnly,
  } = props;
  const { categoryManagementStore } = useStore();
  const { GetCategoryManagementListFilterService } = categoryManagementStore;
  const [showScanner, setShowScanner] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedItems, setSelectedItems] = useState("");
  const [itemDetail, setItemDetail] = useState(
    formatMessage("choose_category")
  );
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBusinessType, setselectedBusinessType] = useState(-1);

  useEffect(() => {
    if (props.id > 0) {
      let path = findObjByKeyWithPath(
        props.categoryOptions,
        props.initialValues?.CategoryId
      )?.path;
      let obj = findObjByKeyWithPath(
        props.categoryOptions,
        props.initialValues?.CategoryId
      )?.obj;
      setSelectedCategory(obj);
      setSelectedItems(obj?.id);
      setItemDetail(path ? path : formatMessage("choose_category"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.categoryOptions, props.initialValues]);

  const formik = useFormikContext();

  useEffect(() => {
    const businessTypeId =
      selectedBusinessType > -1
        ? selectedBusinessType
        : props.initialValues?.BusinessTypeId;

    if (businessTypeId > 0) {
      GetCategoryManagementListFilterService(businessTypeId, 2);
    }

    setSelectedItems("");
    setSelectedCategory(null);
    setItemDetail(formatMessage("choose_category"));

    if (formik) {
      formik.setFieldValue("CategoryPath", "");
      formik.setFieldTouched("CategoryPath", true);
    }
  }, [selectedBusinessType, props.initialValues]);

  const handleDelete = () => {
    deletePopupToggleHandler();
  };

  const handleDeleteModalClosed = () => {
    deletePopupToggleHandler();
  };

  const openScanner = () => {
    setShowScanner(true);
  };

  const closeScanner = () => {
    setShowScanner(false);
  };

  const handleBarcodeScanned = (barcode: string, setFieldValue: any) => {
    setFieldValue("BarCode",toHalfWidth(barcode));
    setShowScanner(false);
  };

  const findObjByKeyWithPath = (
    data: any[],
    key: number
  ): { obj: any; path: string } | null => {
    const search = (
      data: any[],
      key: number,
      path: string[]
    ): { obj: any; path: string } | null => {
      for (const obj of data) {
        const currentPath = [...path, obj.title];

        if (obj.key === key) {
          return { obj, path: currentPath.join(" > ") };
        }

        if (obj.children && obj.children.length > 0) {
          const found = search(obj.children, key, currentPath);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    // Start the search with an empty path
    return search(data, key, []);
  };

  const isValidDate = (date: any) => {
    return dayjs(date).isValid();
  };

  const isOpen = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const formatDate = (date: string | Date) => dayjs(date).format(DATE_FORMAT);

  return (
    <>
      {showDeletePopup && (
        <DeletePopup
          modalSubmit={handleDeleteSubmitHandler}
          modalClosed={handleDeleteModalClosed}
          isLoading={deleteActionLoading}
          title={formatMessage("delete_confirmation")}
          message={formatMessage(
            "are_you_sure_you_want_to_delete_this_product?"
          )}
        />
      )}

      <Modal show={true} centered>
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              <FormattedMessage id="product_registration" />
            ) : (
              <FormattedMessage id="product_details" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.getProductManagementLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            ...props.initialValues,
          }}
          enableReinitialize={true}
          validationSchema={
            props.id === -1
              ? AddProductValidationSchema(selectedCategory)
              : EditProductValidationSchema(selectedCategory)
          }
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={async (values) => {
            let obj = {
              RegistrationType: values.RegistrationType,
              BusinessTypeId: values.BusinessTypeId,
              CategoryId:
                selectedCategory !== null ? Number(selectedItems) : -1,
              ProductName: values.ProductName,
              PriceExcludingTax: values.PriceExcludingTax || 0,
              PriceIncludingTax: toHalfWidth(values.PriceIncludingTax),
              TaxId: values.TaxId,
              TaxValue:
                props.taxOptions.find(
                  (item: IOption) => item.id == values.TaxId
                )?.value ?? 0,
              TaxAmount: values.TaxAmount || 0,
              BarCode: toHalfWidth(values.BarCode),
              ProductCategory: values.ProductCategory,
              Status: values.Status,
              StartDate: values.StartDate
                ? new Date(values.StartDate?.$d).toString() !== "Invalid Date"
                  ? moment(values.StartDate.$d).format(DATE_FORMAT)
                  : values.StartDate
                : "0001-01-01T00:00:00.0000000",
              EndDate: values.EndDate
                ? new Date(values.EndDate?.$d).toString() !== "Invalid Date"
                  ? moment(values.EndDate.$d).format(DATE_FORMAT)
                  : values.EndDate
                : "0001-01-01T00:00:00.0000000",
              Image: values?.Image,
              UnitPrice: toHalfWidth(values?.UnitPrice || 0),
              UnitPriceExcludingTax: values.UnitPriceExcludingTax || 0,
              UnitPriceTaxId: values.UnitPriceTaxId,
              UnitPriceTaxValue:
                props.taxOptions.find(
                  (item: IOption) => item.id == values.UnitPriceTaxId
                )?.value ?? 0,
              UnitPriceTaxAmount: values.UnitPriceTaxAmount || 0,
              Note: values?.Note,
              Description: values?.Description,
              IsCategoryChanged: false
            };
            if(obj.CategoryId !== props.initialValues?.CategoryId)
              obj.IsCategoryChanged = true;
            // Format dates before submission
            // values.StartDate = formatDate(values.StartDate);
            // values.EndDate = formatDate(values.EndDate);
            props.submitHandler(obj);
          }}
        >
          {({ setFieldValue, setFieldTouched, values }) => {
            const handleClick = async (
              event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
              fieldName: string
            ) => {
              const field = fieldName;

              if (field === "PriceExcludingTaxIncreaseBtn") {
                setFieldValue("PriceExcludingTax", parseInt(values.PriceExcludingTax) + 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) - 1); // Uncheck the other checkbox
              } else if (field === "PriceExcludingTaxDecreaseBtn") {
                setFieldValue("PriceExcludingTax", parseInt(values.PriceExcludingTax) - 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) + 1); // Uncheck the other checkbox
              }
              else if (field === "TaxAmountIncreaseBtn") {
                setFieldValue("PriceExcludingTax", parseInt(values.PriceExcludingTax) - 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) + 1); // Uncheck the other checkbox
              } else if (field === "TaxAmountDecreaseBtn") {
                setFieldValue("PriceExcludingTax", parseInt(values.PriceExcludingTax) + 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) - 1); // Uncheck the other checkbox
              }
            };
            
            const handleUnitPriceIncDecBtnClick = async (
              event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
              fieldName: string
            ) => {
              const field = fieldName;

              if (field === "UnitPriceExcludingTaxIncreaseBtn") {
                setFieldValue("UnitPriceExcludingTax", parseInt(values.UnitPriceExcludingTax) + 1);
                setFieldValue("UnitPriceTaxAmount", parseInt(values.UnitPriceTaxAmount) - 1); // Uncheck the other checkbox
              } else if (field === "UnitPriceExcludingTaxDecreaseBtn") {
                setFieldValue("UnitPriceExcludingTax", parseInt(values.UnitPriceExcludingTax) - 1);
                setFieldValue("UnitPriceTaxAmount", parseInt(values.UnitPriceTaxAmount) + 1); // Uncheck the other checkbox
              }
              else if (field === "UnitPriceTaxAmountIncreaseBtn") {
                setFieldValue("UnitPriceExcludingTax", parseInt(values.UnitPriceExcludingTax) - 1);
                setFieldValue("UnitPriceTaxAmount", parseInt(values.UnitPriceTaxAmount) + 1); // Uncheck the other checkbox
              } else if (field === "UnitPriceTaxAmountDecreaseBtn") {
                setFieldValue("UnitPriceExcludingTax", parseInt(values.UnitPriceExcludingTax) + 1);
                setFieldValue("UnitPriceTaxAmount", parseInt(values.UnitPriceTaxAmount) - 1); // Uncheck the other checkbox
              }
            };
            return (
              <Form className="user-form">
                <ProductAutoCalculateTax
                  id={props.id}
                  taxOptions={props.taxOptions}
                  feeExcludingTaxField="PriceExcludingTax"
                  taxIdField="TaxId"
                  taxAmountField="TaxAmount"
                  feeIncludingTaxField="PriceIncludingTax"
                  feeTaxValueField="TaxValue"
                />
                <ProductAutoCalculateTax
                  id={props.id}
                  taxOptions={props.taxOptions}
                  feeExcludingTaxField="UnitPriceExcludingTax"
                  taxIdField="UnitPriceTaxId"
                  taxAmountField="UnitPriceTaxAmount"
                  feeIncludingTaxField="UnitPrice"
                  feeTaxValueField="UnitPriceTaxValue"
                />
                {props.getProductManagementLoading && (
                  <FormLoader loading={props.getProductManagementLoading} />
                )}
                <Modal.Body>
                  {/* Row 1 */}

                  <div className="row">
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="registration_type"
                      />
                      {props.id > 0 ? (
                        <Field
                          name="RegistrationTypeName"
                          as={FormikFormInput}
                          disabled={true}
                          className="form-control"
                          type="text"
                          // validcharacter={["+", "(", ")", "-", "_", "&"]}
                          validcharacter={[
                            "・",
                            "／",
                            "－",
                            "～",
                            "＋",
                            "（",
                            "）",
                            "［",
                            "］",
                            "※",
                            "：",
                            "★",
                            "☆",
                            "◎",
                            "○",
                            "●",
                            "◆",
                            "◇",
                            "■",
                            "□",
                            "→",
                            "⇒",
                            "♪",
                            "(",
                            ")",
                            "【",
                            "】",
                            "￥",
                            '"',
                            "＜",
                            "＞",
                            "！",
                            "？",
                            "↓",
                            "↑",
                            "←",
                            "．",
                            '"',
                            "゛",
                            "'",
                            "'",
                            '"',
                            '"',
                            "、",
                            "。",
                            "《",
                            "》",
                            "+",
                            "-",
                            "[",
                            "]",
                            "|",
                            "*",
                            "&",
                            "/",
                          ]}
                        />
                      ) : (
                        <Field
                          name="RegistrationType"
                          as={FormikFormSelect}
                          options={props.registrationTypeOptions}
                          disabled={true}
                        />
                      )}
                    </div>

                    {getUserTypeFromLocalStorage() !==
                      userTypeEnum[userTypeEnum.HeadQuarter] && (
                      <div className="col-sm-6 form-group">
                        <Label
                          className="label-style"
                          required={true}
                          label="business_type"
                        />
                        <Field
                          name="BusinessTypeId"
                          as={FormikFormOnChangeSelect}
                          setState={setselectedBusinessType}
                          options={props.businessTypeOptions}
                          disabled={
                            getUserTypeFromLocalStorage() ===
                            userTypeEnum[userTypeEnum.HeadQuarter]
                              ? true
                              : viewOnly
                          }
                        />
                      </div>
                    )}
                   
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="category"
                      />
                      {/* <Field
                        name="CategoryId"
                        className="form-control"
                        as={FormikFormSelect}
                        options={props.categoryOptions}
                      /> */}

                      <Field
                        type="text"
                        className="form-control blinkCursor form-select"
                        name="CategoryPath"
                        value={itemDetail}
                        onClick={handleOpen}
                        as={FormikFormInput}
                        onBlur={() => {
                          if (!values.CategoryPath) {
                            setFieldTouched("CategoryPath", true);
                          }
                        }}
                        disabled={viewOnly}
                      />

                      <Popover
                        open={isOpen}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        disableAutoFocus
                      >
                        {props.categoryOptions?.length > 0 ? (
                          <RichTreeView
                            checkboxSelection
                            selectedItems={selectedItems}
                            onSelectedItemsChange={(
                              e: any,
                              newSelectedItems: any
                            ) => {
                              if (newSelectedItems) {
                                const selectedCategoryObj =
                                  findObjByKeyWithPath(
                                    props.categoryOptions,
                                    Number(newSelectedItems)
                                  )?.obj;

                                const categoryPath = findObjByKeyWithPath(
                                  props.categoryOptions,
                                  Number(newSelectedItems)
                                )?.path!;

                                setSelectedItems(newSelectedItems);
                                setSelectedCategory(selectedCategoryObj);
                                setItemDetail(categoryPath);

                                setFieldValue("CategoryPath", categoryPath);
                                setFieldTouched("CategoryPath", false, false);
                              } else {
                                setSelectedItems("");
                                setSelectedCategory(null);
                                setItemDetail(formatMessage("choose_category"));

                                setFieldValue("CategoryPath", "");
                                setFieldTouched("CategoryPath", true);
                              }
                              handleClose();
                            }}
                            items={props.categoryOptions}
                          />
                        ) : (
                          formatMessage("no_category_added")
                        )}
                      </Popover>
                    </div>
                    <div className="col-sm-12 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="product_name"
                      />
                      <Field
                        name="ProductName"
                        type="text"
                        validcharacter={[
                          "+",
                          "-",
                          "[",
                          "]",
                          "|",
                          "*",
                          "&",
                          "/",
                        ]}
                        className="form-control"
                        placeholder="enter_product_name"
                        as={FormikFormInput}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="product_category"
                      />
                      <div className="row">
                        <div className="col-6">
                          <Field
                            className="form-check-input"
                            name="ProductCategory"
                            type="radio"
                            value="0"
                            id="merchandise"
                            disabled={viewOnly}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="merchandise"
                          >
                            <FormattedMessage id="merchandise" />
                          </label>
                        </div>
                        <div className="col-6">
                          <Field
                            className="form-check-input"
                            name="ProductCategory"
                            type="radio"
                            value="1"
                            id="others"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="others">
                            <FormattedMessage id="others" />
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="bar_code"
                      />
                      <div className="d-flex align-items-start">
                        <div className="flex-grow-1">
                          <Field
                            name="BarCode"
                            className="form-control"
                            type="text"
                            placeholder="bar_code"
                            as={FormikFormInput}
                            disabled={viewOnly}
                            validcharacter={[
                              "$",
                              "%",
                              "*",
                              "+",
                              "-",
                              ".",
                              "/",
                              ")",
                              ":",
                              "(",
                              " ",
                            ]}
                          />
                        </div>
                        <Button
                          variant="primary"
                          onClick={openScanner}
                          className="ms-2"
                          disabled={viewOnly}
                        >
                          <i className="bi bi-camera-fill"></i>
                        </Button>
                      </div>
                    </div>

                    {/* <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="sales_price"
                      />
                      <Field
                        name="SalesPrice"
                        type="text"
                        className="form-control"
                        placeholder="enter_sales_price"
                        regex={integerRegex}
                        as={FormikFormInputForRegexCheck}
                      />
                    </div> */} 
                  <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="unit_price(including_tax)"
                      />
                      <Field
                        name="UnitPrice"
                        className="form-control"
                        type="text"
                        placeholder="enter_unit_price"
                        regex={integerRegex}
                        as={FormikFormInputRegexCheckFormattedNumber}
                        disabled={viewOnly}
                      />
                    </div>

                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="unit_price_tax"
                      />
                      <Field
                        name="UnitPriceTaxId"
                        as={FormikFormSelect}
                        options={props.taxOptions}
                        disabled={viewOnly}
                      />
                    </div>
                  
                    <div className="col-sm-6 form-group">
                      <Label className="label-style" label="unit_price_tax_amount" />
                      <div className="input-group">
                        <Field
                          name="UnitPriceTaxAmount"
                          className="form-control"
                          type="text"
                          regex={integerRegex}
                          as={FormikFormInputRegexCheckFormattedNumber}
                          disabled={true}
                        />
                        <button
                        name="UnitPriceTaxAmountDecreaseBtn"
                        type="button"
                        onClick={(e) =>
                          handleUnitPriceIncDecBtnClick(e, "UnitPriceTaxAmountDecreaseBtn")
                        }
                        className="btn btn-dark"
                        disabled={(Math.round(parseFloat(values.UnitPrice) - (parseFloat(values.UnitPrice) / (1 + (values.UnitPriceTaxValue / 100)))) - 1 === values.UnitPriceTaxAmount) || (values.UnitPriceExcludingTax == 0 || values.UnitPriceTaxAmount == 0) || viewOnly}
                      >
                        <i className="bi bi-dash-lg"></i>
                      </button>
                      <button
                        name="UnitPriceTaxAmountIncreaseBtn"
                        type="button"
                        onClick={(e) =>
                          handleUnitPriceIncDecBtnClick(e, "UnitPriceTaxAmountIncreaseBtn")
                        }
                        className="btn btn-dark"
                        disabled={(Math.round(parseFloat(values.UnitPrice) - parseFloat(values.UnitPrice) / (1 + (values.UnitPriceTaxValue / 100))) + 1 === values.UnitPriceTaxAmount) || (values.UnitPriceExcludingTax == 0 || values.UnitPriceTaxAmount == 0) || viewOnly}
                      >
                        <i className="bi bi-plus-lg"></i>
                      </button>
                    </div>
                    </div>
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        label="unit_price(excluding_tax)"
                      />
                      <div className="input-group">
                        <Field
                          name="UnitPriceExcludingTax"
                          className="form-control"
                          type="text"
                          disabled={true}
                          regex={integerRegex}
                          as={FormikFormInputRegexCheckFormattedNumber}
                        />
                        <button
                        name="UnitPriceExcludingTaxDecreaseBtn"
                        className="btn btn-dark"
                        type="button"
                        onClick={(e) =>
                          handleUnitPriceIncDecBtnClick(e, "UnitPriceExcludingTaxDecreaseBtn")
                        }
                        disabled={(Math.round(parseFloat(values.UnitPrice) / (1 + (values.UnitPriceTaxValue / 100))) - 1 === values.UnitPriceExcludingTax) || (values.UnitPriceExcludingTax == 0 || values.UnitPriceTaxAmount == 0) || viewOnly}
                      >
                        <i className="bi bi-dash-lg"></i>
                      </button>
                      <button
                        name="UnitPriceExcludingTaxIncreaseBtn"
                        className="btn btn-dark"
                        type="button"
                        onClick={(e) =>
                          handleUnitPriceIncDecBtnClick(e, "UnitPriceExcludingTaxIncreaseBtn")
                        }
                        disabled={(Math.round(parseFloat(values.UnitPrice) / (1 + (values.UnitPriceTaxValue / 100))) + 1 === values.UnitPriceExcludingTax)  || (values.UnitPriceExcludingTax == 0 || values.UnitPriceTaxAmount == 0) || viewOnly}
                      >
                        <i className="bi bi-plus-lg"></i>
                      </button>
                    </div>
                    </div> 
                  {/* Row 4 */}
                   
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="price(including_tax)"
                      />
                      <Field
                        name="PriceIncludingTax"
                        className="form-control"
                        type="text"
                        placeholder="enter_unit_price"
                        regex={integerRegex}
                        as={FormikFormInputRegexCheckFormattedNumber}
                        disabled={viewOnly}
                      />
                    </div>

                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="tax"
                      />
                      <Field
                        name="TaxId"
                        as={FormikFormSelect}
                        options={props.taxOptions}
                        disabled={viewOnly}
                      />
                    </div>
                  
                    <div className="col-sm-6 form-group">
                      <Label className="label-style" label="tax_amount" />
                      <div className="input-group">
                        <Field
                          name="TaxAmount"
                          className="form-control"
                          type="text"
                          disabled={true}
                          regex={integerRegex}
                          as={FormikFormInputRegexCheckFormattedNumber}
                        />
                        <button
                        name="TaxAmountDecreaseBtn"
                        type="button"
                        onClick={(e) =>
                          handleClick(e, "TaxAmountDecreaseBtn")
                        }
                        className="btn btn-dark"
                        disabled={(Math.round(parseFloat(values.PriceIncludingTax) - (parseFloat(values.PriceIncludingTax) / (1 + (values.TaxValue / 100)))) - 1 === values.TaxAmount) || (values.PriceExcludingTax == 0 || values.TaxAmount == 0) || viewOnly}
                      >
                        <i className="bi bi-dash-lg"></i>
                      </button>
                      <button
                        name="TaxAmountIncreaseBtn"
                        type="button"
                        onClick={(e) =>
                          handleClick(e, "TaxAmountIncreaseBtn")
                        }
                        className="btn btn-dark"
                        disabled={(Math.round(parseFloat(values.PriceIncludingTax) - parseFloat(values.PriceIncludingTax) / (1 + (values.TaxValue / 100))) + 1 === values.TaxAmount) || (values.PriceExcludingTax == 0 || values.TaxAmount == 0) || viewOnly}
                      >
                        <i className="bi bi-plus-lg"></i>
                      </button>
                    </div>
                    </div>
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        label="price(excluding_tax)"
                      />
                      <div className="input-group">
                        <Field
                          name="PriceExcludingTax"
                          className="form-control"
                          type="text"
                          disabled={true}
                          regex={integerRegex}
                          as={FormikFormInputRegexCheckFormattedNumber}
                        />
                        <button
                        name="PriceExcludingTaxDecreaseBtn"
                        className="btn btn-dark"
                        type="button"
                        onClick={(e) =>
                          handleClick(e, "PriceExcludingTaxDecreaseBtn")
                        }
                        disabled={(Math.round(parseFloat(values.PriceIncludingTax) / (1 + (values.TaxValue / 100))) - 1 === values.PriceExcludingTax) || (values.PriceExcludingTax == 0 || values.TaxAmount == 0) || viewOnly}
                      >
                        <i className="bi bi-dash-lg"></i>
                      </button>
                      <button
                        name="PriceExcludingTaxIncreaseBtn"
                        className="btn btn-dark"
                        type="button"
                        onClick={(e) =>
                          handleClick(e, "PriceExcludingTaxIncreaseBtn")
                        }
                        disabled={(Math.round(parseFloat(values.PriceIncludingTax) / (1 + (values.TaxValue / 100))) + 1 === values.PriceExcludingTax)  || (values.PriceExcludingTax == 0 || values.TaxAmount == 0) || viewOnly}
                      >
                        <i className="bi bi-plus-lg"></i>
                      </button>
                    </div>
                    </div> 

                  <div className="col-12 form-group">
                    <Label className="label-style" label="Description" />
                    <Field
                      name="Description"
                      as={FormikFormTextArea}
                      className="form-control"
                      placeholder="enter_description"
                      disabled={viewOnly}
                      validcharacter={[
                        "・",
                        "／",
                        "－",
                        "～",
                        "＋",
                        "（",
                        "）",
                        "［",
                        "］",
                        "※",
                        "：",
                        "★",
                        "☆",
                        "◎",
                        "○",
                        "●",
                        "◆",
                        "◇",
                        "■",
                        "□",
                        "→",
                        "⇒",
                        "♪",
                        "(",
                        ")",
                        "【",
                        "】",
                        "￥",
                        '"',
                        "＜",
                        "＞",
                        "！",
                        "？",
                        "↓",
                        "↑",
                        "←",
                        "．",
                        '"',
                        "゛",
                        "'",
                        "'",
                        '"',
                        '"',
                        "、",
                        "。",
                        "《",
                        "》",
                        "+",
                        "-",
                        "[",
                        "]",
                        "|",
                        "*",
                        "&",
                        "/",
                        ".",
                        "!",
                        "@",
                        "#",
                        "$",
                        "%",
                        "^",
                        "_",
                        "`",
                        "{",
                        "}",
                        "?",
                        ";",
                        ":",
                        ",",
                        "=",
                        "~",
                        "<",
                        ">",
                      ]}
                    />
                    <p className="small text-muted text-end mt-1">{`${
                      values.Description ? values.Description.length : 0
                    }/300`}</p>
                  </div>

                  {/* Row 6 */}
                 
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="start_date"
                      />
                      <Field
                        name="StartDate"
                        as={FormikDateFieldMUI}
                        // min={dayjs().format(DATE_FORMAT)}
                        disableFuture={false}
                        disabled={viewOnly}
                      />
                    </div>

                    {/* End Date */}
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="end_date"
                      />
                      <Field
                        name="EndDate"
                        as={FormikDateFieldMUI}
                        // min={dayjs().format(DATE_FORMAT)}
                        disableFuture={false}
                        disabled={viewOnly}
                      />
                    </div>
                   
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="upload_image"
                      />
                      <div className="col-sm-4 mb-3">
                        <Field
                          className="form-control"
                          name="Image"
                          type="text"
                          as={FormikFormImagePicker}
                          disabled={viewOnly}
                        />
                      </div>
                    </div>
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="remarks"
                      />
                      <Field
                        name="Note"
                        id="CounsellingNote"
                        as={FormikFormTextArea}
                        className="form-control"
                        placeholder="enter_remarks"
                        type="text"
                        row={6}
                        disabled={viewOnly}
                      />
                      <p className="small text-muted text-end mt-1">{`${
                        values.Note ? values.Note.length : 0
                      }/300`}</p>
                    </div>
                   
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="status"
                      />
                      <div className="row">
                        <div className="col-6">
                          <Field
                            className="form-check-input"
                            name="Status"
                            type="radio"
                            value="0"
                            id="valid"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="valid">
                            <FormattedMessage id="valid" />
                          </label>
                        </div>
                        <div className="col-6">
                          <Field
                            className="form-check-input"
                            name="Status"
                            type="radio"
                            value="1"
                            id="invalid"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="invalid">
                            <FormattedMessage id="invalid" />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </Modal.Body>

                {/* Footer Buttons */}
                <div className="modal-footer center-content">
                  <Button
                    variant="outline-danger"
                    type="button"
                    onClick={props.onModalClosed}
                    disabled={props.getProductManagementLoading}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    <FormattedMessage id="close" />
                  </Button>
                  {
                    !viewOnly &&
                    <>
                      {isOperationPermittedRoleBase(
                        ModuleType.ProductManagement,
                        ActionType.Edit
                      ) && (
                        <LoaderButton
                          type="submit"
                          iconName="bi-check-lg"
                          isLoading={props.getProductManagementLoading}
                          disabled={props.viewOnly}
                          text={props.id > -1 ? "button_update" : "button_register"}
                        />
                      )}
                      {props.id > -1 &&
                        isOperationPermittedRoleBase(
                          ModuleType.ProductManagement,
                          ActionType.Delete
                        ) && (
                          <Button
                            variant="danger"
                            type="button"
                            onClick={handleDelete}
                            disabled={props.getProductManagementLoading || props.viewOnly}
                          >
                            <i className="bi bi-trash3-fill me-1"></i>
                            <FormattedMessage id="button_delete" />
                          </Button>
                        )}
                    </>
                  }
                </div>

                {/* Barcode Scanner Modal */}
                <BarcodeScannerModal
                  show={showScanner}
                  onClose={closeScanner}
                  onScan={(barcode) =>
                    handleBarcodeScanned(barcode, setFieldValue)
                  }
                />
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default DeletePopupHOC(AddProductManagementForm);
